import { createFileRoute, Link } from "@tanstack/react-router";
import { ArticleLayout } from "@/components/ArticleLayout";

import { ogImageFor, ogMetaTags, twitterTextTags } from "@/lib/ogImages";
import { getArticleBySlug } from "@/lib/learningHub";

const SITE_URL = "https://coloradowebimpressions.com";
const ARTICLE = getArticleBySlug("/learn/get-more-reviews");
const OG_IMAGE = `${SITE_URL}${ogImageFor({ articleCategory: ARTICLE?.category })}`;

export const Route = createFileRoute("/learn/get-more-reviews")({
  head: () => ({
    meta: [
        { name: "description", content: "Step-by-step playbook to get more 5-star Google reviews from real Colorado customers — without bribes, gimmicks, or violating Google's policies." },
      {
        title: "How to Get 5-Star Google Reviews in Colorado",
      },
      {
        name: "description",
        content:
          "Exact ask, timing, and templates for getting more Google reviews — and why review-gating will get your account suspended.",
      },
      {
        property: "og:title",
        content: "How to Get More 5-Star Google Reviews — Colorado Guide",
      },
      {
        property: "og:description",
        content:
          "The honest playbook for review generation we use with Colorado service businesses.",
      },
      { property: "og:url", content: `${SITE_URL}/learn/get-more-reviews` },
      { property: "og:type", content: "article" },
      ...ogMetaTags(OG_IMAGE),
      ...twitterTextTags({
        title: ARTICLE?.title ?? "Colorado Web Impressions",
        description: ARTICLE?.desc ?? "Honest answers from a 25-year Colorado agency.",
      }),
    ],
    links: [{ rel: "canonical", href: "https://coloradowebimpressions.com/learn/get-more-reviews" }],
  }),
  component: Article,
});

function Article() {
  return (
    <ArticleLayout
      eyebrow="Reviews & Reputation"
      read="7 min read"
      title="How to get 5-star Google reviews from Colorado customers (the right way)"
      intro="Reviews are the #1 trust signal in local search. Volume, recency, star rating, and your responses all directly affect both your conversion rate and your Google Maps ranking. Here&rsquo;s how to get more — without breaking Google&rsquo;s rules."
      breadcrumbs={[
        { label: "Home", to: "/" },
        { label: "Learning Hub", to: "/learn" },
        { label: "Get more reviews" },
      ]}
      path="/learn/get-more-reviews"
      faqs={[
        { q: "Is it against Google's rules to ask for reviews?", a: "No. Asking is allowed and encouraged. What's not allowed is offering money, discounts, or gifts in exchange for a review, or filtering customers so only happy ones are asked." },
        { q: "How many Google reviews do I need to compete?", a: "In most Colorado service markets, 50+ reviews with a 4.7+ star average puts you in the conversation. 100+ with steady recent activity is where you start to dominate." },
        { q: "What do I do about a fake or unfair review?", a: "Flag it through Google, respond professionally and publicly, and keep generating new positive reviews. One bad review buried under 30 fresh 5-stars stops mattering quickly." },
      ]}
    >
      <h2>The numbers that actually matter</h2>
      <ul>
        <li><strong>Volume:</strong> 50+ reviews puts you ahead of most local competitors.</li>
        <li><strong>Recency:</strong> A steady drip of 4–8 reviews per month signals an active business.</li>
        <li><strong>Star rating:</strong> 4.7+ is the sweet spot. A perfect 5.0 with 200 reviews actually looks fake to a lot of buyers.</li>
        <li><strong>Response rate:</strong> 100% reply rate within 48 hours. Both 5-star and 1-star.</li>
      </ul>

      <h2>The ask — when, how, and what to say</h2>

      <h3>When</h3>
      <p>
        Right when the customer is happiest. For service work, that&rsquo;s the moment the job is
        done and they&rsquo;ve confirmed they&rsquo;re happy. For longer engagements, it&rsquo;s
        the first &ldquo;wow&rdquo; moment — not at invoice time.
      </p>

      <h3>How</h3>
      <p>
        Three formats, in order of effectiveness:
      </p>
      <ol>
        <li><strong>In-person verbal ask</strong> from the technician/team member who did the work, while they&rsquo;re still on-site. Send a text with the link before they leave.</li>
        <li><strong>Text message</strong> within 1 hour of job completion with a direct review link.</li>
        <li><strong>Email</strong> within 24 hours as a backup.</li>
      </ol>

      <h3>The exact text we use</h3>
      <p>
        &ldquo;Hi [Name] — really enjoyed working with you today. If we earned it, would you mind
        leaving us a quick Google review? It honestly takes 30 seconds and makes a huge difference
        for a small business like ours. Here&rsquo;s the link: [link]. Thank you! — [Tech name]&rdquo;
      </p>
      <p>
        Three rules: name them, name yourself, make it short.
      </p>

      <h2>What NOT to do (Google will suspend you)</h2>
      <ul>
        <li><strong>Review gating</strong> — sending happy customers to Google and unhappy ones to a private form. Google explicitly bans this. They check.</li>
        <li><strong>Buying reviews</strong> — Fiverr, review farms, &ldquo;$5 for 5 stars.&rdquo; Google&rsquo;s detection is way better than agencies admit. You&rsquo;ll lose every review and possibly your profile.</li>
        <li><strong>Incentivizing</strong> — &ldquo;$10 off for a 5-star review.&rdquo; Hard violation.</li>
        <li><strong>Asking employees / family / friends</strong> who never bought from you — Google flags these by IP and account history.</li>
        <li><strong>Asking everyone in one week</strong> — a sudden spike of 30 reviews after a year of zero looks fake to the algorithm.</li>
      </ul>

      <h2>Responding to reviews</h2>
      <p>
        Every review gets a response. Within 48 hours.
      </p>
      <ul>
        <li><strong>5-star:</strong> 1–2 sentences. Use their first name. Mention something specific (the service, the city). Don&rsquo;t copy-paste.</li>
        <li><strong>4-star:</strong> Thank them, then ask what would have made it a 5.</li>
        <li><strong>3-star or below:</strong> Acknowledge, do not get defensive, offer to make it right offline. Future buyers read your <em>response</em> as much as the review.</li>
      </ul>

      <h2>How reviews tie into ranking</h2>
      <p>
        Reviews directly feed your{" "}
        <Link to="/learn/google-business-profile" className="font-bold text-brand-gold-deep underline">
          Google Business Profile ranking
        </Link>{" "}
        in three ways: total count (prominence), review keywords (relevance), and recency
        (activity). They also dramatically lift conversion on your website — see our piece on{" "}
        <Link to="/learn/website-conversion" className="font-bold text-brand-gold-deep underline">
          why traffic isn&rsquo;t turning into calls
        </Link>
        .
      </p>

      <h2>Want it automated, in-house?</h2>
      <p>
        Our{" "}
        <Link to="/colorado-springs-google-maps" className="font-bold text-brand-gold-deep underline">
          Google Maps service
        </Link>{" "}
        includes review request automation, response writing, and monthly reporting — all done by
        our Colorado team, never outsourced.
      </p>
    </ArticleLayout>
  );
}
